import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { HbrTab, HbrTabGroup, HbrTabPanel } from '@harbor/elements-react-standalone';
import StbButton from '../security-toolbox-ui/StbButton';
import { useLabScenario } from '../../hooks/lab-hooks';
import { LabScenario, labScenarioCustomer } from '../../support/labService';
import LabScenarioEditorModal from './LabScenarioEditorModal';
import {
  LabScenarioCriteriaPanel,
  LabScenarioDevicesPanel,
  LabScenarioStepsPanel,
  LabScenarioTriggersPanel,
} from './LabScenarioPanels';
import { LabCard, LabPanelState, LabStatusTag, labDuration } from './LabShared';

type Tab = 'steps' | 'criteria' | 'devices' | 'triggers';

export type LabScenarioDetailProps = {
  /** Omit to read the id from the route. */
  scenarioId?: number;
  /**
   * Authoring and pushing. Reading a scenario is open; changing one is not, and neither is
   * sending a step at pods that are already running. The API refuses either way - this decides
   * whether the buttons are there to click.
   */
  canEdit?: boolean;
};

/**
 * One scenario, in the four parts it is made of.
 *
 * The parts themselves live in `LabScenarioPanels` because the admin area arranges the same
 * four differently - steps split by phase across its Scenario and Validation tabs, criteria on
 * a Checks tab. This is the lab area's arrangement of them, nothing more.
 */
const LabScenarioDetail: React.FC<LabScenarioDetailProps> = ({ scenarioId, canEdit = false }) => {
  const params = useParams();
  const navigate = useNavigate();
  const id = scenarioId ?? Number(params.scenarioId);

  const [tab, setTab] = useState<Tab>('steps');
  const [scenarioEditorOpen, setScenarioEditorOpen] = useState(false);

  const { data: scenario, isLoading, error, refetch } = useLabScenario(id);

  if (isLoading || error || !scenario) {
    return (
      <LabCard title="Scenario">
        <LabPanelState
          loading={isLoading}
          error={error}
          empty={!scenario}
          emptyMessage="That scenario does not exist."
          loadingLabel="Loading scenario..."
        >
          <span />
        </LabPanelState>
      </LabCard>
    );
  }

  const loaded = scenario as LabScenario;
  const customer = labScenarioCustomer(loaded);
  // A scenario no longer owns where it runs. It is a section of labs, and each of those labs is
  // published per data centre - so the reach question is answered one tier up. Only the links
  // that are live on both ends count: a disabled link, or a section attached to a disabled lab,
  // delivers nothing.
  const labLinks = (loaded.lab_links || []).filter(
    (link) => link.enabled && link.lab_enabled !== false,
  );
  const legacyConfigurations = loaded.legacy_configurations || [];

  return (
    <>
      <div className="flex flex-col gap-4">
        <LabCard
          title={loaded.name}
          subtitle={
            <span>
              {loaded.slug}
              {` • version ${loaded.version}`}
              {` • pass at ${loaded.pass_threshold_percent}%`}
              {customer && ` • ${customer}`}
            </span>
          }
          actions={
            <>
              <StbButton role="default" size="small" onClick={async () => navigate('/lab/scenarios')}>
                All scenarios
              </StbButton>
              <StbButton role="default" size="small" onClick={async () => refetch()}>
                Refresh
              </StbButton>
              {canEdit && (
                <StbButton
                  role="update"
                  size="small"
                  onClick={async () => setScenarioEditorOpen(true)}
                >
                  Edit scenario
                </StbButton>
              )}
            </>
          }
        >
          {loaded.description && <p className="m-0 text-sm text-gray-700">{loaded.description}</p>}

          <div className="mt-2 flex flex-wrap gap-1">
            {!loaded.enabled && <LabStatusTag label="Disabled" sentiment="warning" />}
            {loaded.auto_start ? (
              <LabStatusTag label="Runs open automatically" sentiment="info" />
            ) : (
              <LabStatusTag label="No run opens by itself" sentiment="warning" />
            )}
            {/* A scenario reaches sessions through a lab that includes it, through an older
                publication that named it directly, or through its own demo id. None of the three
                means nothing will ever match it. */}
            {!labLinks.length && !legacyConfigurations.length && !loaded.demo_uid && (
              <LabStatusTag
                label="Not in any lab - sessions will not match"
                sentiment="warning"
              />
            )}
            {labLinks.length > 0 && (
              <LabStatusTag
                label={`A section of ${labLinks.length} lab${labLinks.length === 1 ? '' : 's'}`}
                sentiment="info"
              />
            )}
            {/* Worth showing on its own rather than folded into the count above: a publication
                that names this scenario directly predates the lab tier, and is the one binding
                nothing new should be added to. */}
            {legacyConfigurations.length > 0 && (
              <LabStatusTag
                label={`Published directly in ${legacyConfigurations.length} data centre${
                  legacyConfigurations.length === 1 ? '' : 's'
                } (pre-lab)`}
                sentiment="warning"
              />
            )}
            {loaded.bootstrap_only && <LabStatusTag label="Bootstrap only" sentiment="info" />}
          </div>

          <div className="mt-3 text-xs text-gray-500">
            {`Setup timeout ${labDuration(loaded.setup_timeout_seconds)} • validation every ${labDuration(
              loaded.validation_interval_seconds,
            )}`}
          </div>
        </LabCard>

        <HbrTabGroup primary>
          <HbrTab slot="nav" panel="steps" active={tab === 'steps'} onClick={() => setTab('steps')}>
            Steps
          </HbrTab>
          <HbrTab
            slot="nav"
            panel="criteria"
            active={tab === 'criteria'}
            onClick={() => setTab('criteria')}
          >
            Success criteria
          </HbrTab>
          <HbrTab
            slot="nav"
            panel="devices"
            active={tab === 'devices'}
            onClick={() => setTab('devices')}
          >
            Devices
          </HbrTab>
          <HbrTab
            slot="nav"
            panel="triggers"
            active={tab === 'triggers'}
            onClick={() => setTab('triggers')}
          >
            Triggers
          </HbrTab>

          <HbrTabPanel name="steps">
            {tab === 'steps' && <LabScenarioStepsPanel scenario={loaded} canEdit={canEdit} />}
          </HbrTabPanel>

          <HbrTabPanel name="criteria">
            {tab === 'criteria' && <LabScenarioCriteriaPanel scenario={loaded} canEdit={canEdit} />}
          </HbrTabPanel>

          <HbrTabPanel name="devices">
            {tab === 'devices' && <LabScenarioDevicesPanel scenario={loaded} canEdit={canEdit} />}
          </HbrTabPanel>

          <HbrTabPanel name="triggers">
            {tab === 'triggers' && <LabScenarioTriggersPanel scenario={loaded} canEdit={canEdit} />}
          </HbrTabPanel>
        </HbrTabGroup>
      </div>

      <LabScenarioEditorModal
        open={scenarioEditorOpen}
        scenario={loaded}
        onClose={() => setScenarioEditorOpen(false)}
      />
    </>
  );
};

export default LabScenarioDetail;
